/** Shared field / option explanations for CRM and related forms. */

export type HintOption = { label: string; meaning: string };

/** Build a multi-line tooltip: summary, then each option on its own line. */
export function withOptions(summary: string, options: HintOption[]): string {
  return [summary, "", ...options.map((item) => `${item.label} — ${item.meaning}`)].join("\n");
}

/** Match a status/value to its meaning (handles `pending` vs `Pending` and underscores). */
export function optionMeaning(options: HintOption[], value: string | null | undefined): string | undefined {
  if (!value?.trim()) return undefined;
  const raw = value.trim().toLowerCase();
  const spaced = raw.split("_").join(" ");
  const hit = options.find((item) => {
    const label = item.label.toLowerCase();
    return label === raw || label === spaced || label.split(" ").join("_") === raw;
  });
  return hit?.meaning;
}

/** Single-line title for a status chip: "Pending — …" when a meaning exists. */
export function statusTitle(options: HintOption[], value: string | null | undefined): string | undefined {
  const meaning = optionMeaning(options, value);
  if (!value) return meaning;
  if (!meaning) return undefined;
  return `${value.split("_").join(" ")} — ${meaning}`;
}

export const OPPORTUNITY_STAGE_OPTIONS: HintOption[] = [
  { label: "Lead In", meaning: "Deal just entered the pipeline; discovery not finished." },
  { label: "Qualifying", meaning: "Confirming need, budget, authority, and timing." },
  { label: "Proposal", meaning: "A formal offer or solution is being prepared or sent." },
  { label: "Negotiation", meaning: "Commercial terms are being discussed; a quote is usually in play." },
  { label: "Closed Won", meaning: "Customer accepted; deal is won." },
  { label: "Closed Lost", meaning: "Deal did not close; opportunity is lost." },
];

export const FORECAST_CATEGORY_OPTIONS: HintOption[] = [
  { label: "Commit", meaning: "You expect this deal to close in the forecast period — counts toward commit." },
  { label: "Upside", meaning: "Possible win, but not firm enough to commit yet." },
  { label: "Pipeline", meaning: "Earlier or less certain deals; default until you promote to upside or commit." },
];

export const LEAD_TEMPERATURE_OPTIONS: HintOption[] = [
  { label: "Cold", meaning: "Little interest or early contact — low urgency." },
  { label: "Warm", meaning: "Engaged and worth following up — default for most new leads." },
  { label: "Hot", meaning: "Strong buying signals; prioritize outreach." },
];

export const LEAD_SOURCE_OPTIONS: HintOption[] = [
  { label: "Marketing response", meaning: "Reply or engagement from a campaign." },
  { label: "Web form", meaning: "Submitted via your website or landing page." },
  { label: "Referral", meaning: "Introduced by a customer, partner, or contact." },
  { label: "Trade show", meaning: "Met at an event or exhibition." },
  { label: "Outbound", meaning: "Your team initiated contact (call, email, LinkedIn)." },
  { label: "Partner", meaning: "Came through a channel or reseller partner." },
  { label: "Support", meaning: "Raised from a support conversation." },
  { label: "Other", meaning: "Does not fit the listed sources — type a custom label if needed." },
];

/** Values stored on crm2_activities. Cold call, email, meeting, and demo match sales-plan KPIs. */
export const LOGGED_ACTIVITY_TYPES: Array<{ value: string; label: string; meaning: string }> = [
  {
    value: "cold_call",
    label: "Cold call",
    meaning: "First outbound call on a company or contact. Counted on the owner's sales plan as Cold calls. Older rows saved as Call still count.",
  },
  {
    value: "email",
    label: "Email",
    meaning: "Email sent or received on a company or contact. Counted on the owner's sales plan as Emails. Mail sent from the mailbox is included when you own that activity.",
  },
  {
    value: "meeting",
    label: "Meeting",
    meaning: "In-person or video meeting. Counted on the owner's sales plan as Meetings.",
  },
  {
    value: "demo",
    label: "Demo",
    meaning: "Product demonstration. Counted on the owner's sales plan as Demos.",
  },
  {
    value: "note",
    label: "Note",
    meaning: "Internal note. Not counted on the sales plan.",
  },
];

export const ACTIVITY_TYPE_OPTIONS: HintOption[] = LOGGED_ACTIVITY_TYPES.map(({ label, meaning }) => ({ label, meaning }));

/** Outcomes the sales plan can count. Connected, conversation, and discovery feed Meaningful conversations. */
export const ACTIVITY_OUTCOMES: Array<{ value: string; label: string }> = [
  { value: "connected", label: "Connected" },
  { value: "conversation", label: "Conversation" },
  { value: "discovery", label: "Discovery" },
  { value: "no_answer", label: "No answer" },
  { value: "voicemail", label: "Left voicemail" },
  { value: "not_interested", label: "Not interested" },
];

export function activityTypeLabel(value: string | null | undefined): string {
  const raw = (value || "").trim().toLowerCase();
  const found = LOGGED_ACTIVITY_TYPES.find((item) => item.value === raw);
  if (found) return found.label;
  if (raw === "call") return "Call";
  if (raw === "follow_up") return "Follow up";
  if (!raw) return "—";
  return raw.split("_").join(" ");
}

export const ORDER_STATUS_OPTIONS: HintOption[] = [
  { label: "Pending", meaning: "Accepted commercially but not yet confirmed for delivery. You can still edit the order." },
  { label: "Confirmed", meaning: "Customer and internal teams have locked the order. Open it read-only with View." },
  { label: "Fulfilled", meaning: "Goods or services have been delivered. Open it read-only with View." },
  { label: "Cancelled", meaning: "Order will not be fulfilled. Open it read-only with View." },
];

export const INVOICE_STATUS_OPTIONS: HintOption[] = [
  { label: "Draft", meaning: "Internal only — not yet sent to the customer." },
  { label: "Issued", meaning: "Sent to the customer; payment is outstanding. Use Mark paid when funds arrive." },
  { label: "Paid", meaning: "Payment recorded in full. Use Undo paid only to correct a mistake." },
  { label: "Void", meaning: "Cancelled or invalidated; do not collect against this invoice." },
];

export const QUOTE_STATUS_OPTIONS: HintOption[] = [
  { label: "Draft", meaning: "Being prepared; editable. Not yet with the customer." },
  { label: "Pending approval", meaning: "Waiting for an internal approver before it can be sent." },
  { label: "Approved", meaning: "Internally approved and ready to send to the client." },
  { label: "Sent", meaning: "Delivered to the client; waiting for accept or reject." },
  { label: "Accepted", meaning: "Client accepted — you can generate an order." },
  { label: "Rejected", meaning: "Declined or returned for revision; editable again." },
  { label: "Expired", meaning: "Past the expiry date; revise or create a new quote." },
];

export const LEAD_STATUS_OPTIONS: HintOption[] = [
  { label: "New", meaning: "Just captured; not yet worked." },
  { label: "Working", meaning: "Sales is actively following up." },
  { label: "Qualified", meaning: "Fit confirmed; ready to convert to an opportunity." },
  { label: "Unqualified", meaning: "Not a fit right now (same idea as disqualified in some orgs)." },
  { label: "Converted", meaning: "Became an opportunity (and usually a company/contact)." },
  { label: "Disqualified", meaning: "Closed out as not pursuing — reason is stored on the lead." },
];

export const COMPANY_STATUS_OPTIONS: HintOption[] = [
  { label: "Prospect", meaning: "Potential customer; not yet buying." },
  { label: "Active", meaning: "Current customer, or an account you are actively selling to." },
  { label: "Incomplete", meaning: "Created from a lead or import with missing required details. Finish the record." },
  { label: "Dormant", meaning: "Gone quiet. No selected activity for the number of days set in Company settings. Still a customer you may re-engage." },
  { label: "Inactive", meaning: "No longer being worked. Company settings can set this after a longer quiet period, and can start a sales cycle to win the account back." },
  { label: "Merged", meaning: "Combined into another company. Do not use for new deals." },
];

export const TICKET_STATUS_OPTIONS: HintOption[] = [
  { label: "Open", meaning: "Awaiting work or a reply from your team." },
  { label: "Waiting", meaning: "Parked while waiting on the customer or a third party." },
  { label: "Closed", meaning: "Resolved or completed — no further action expected." },
];

export const CYCLE_STAGE_STATUS_OPTIONS: HintOption[] = [
  { label: "Started", meaning: "This stage is active in the timed flow — work its tasks on the map." },
  { label: "Not started", meaning: "Stage has not begun yet. It opens by date or when you start it here." },
  { label: "Done", meaning: "All tasks in this stage are complete; the stage is closed." },
];

export const CYCLE_TASK_STATUS_OPTIONS: HintOption[] = [
  { label: "To do", meaning: "Not started — move here to reopen work." },
  { label: "Doing", meaning: "In progress right now." },
  { label: "Done", meaning: "Finished — counts toward cycle progress." },
];

export const CYCLE_RUN_STATUS_OPTIONS: HintOption[] = [
  { label: "Active", meaning: "Sales cycle is in progress for this company." },
  { label: "Completed", meaning: "All stages finished for this run." },
  { label: "Cancelled", meaning: "Run was abandoned; start a new cycle if needed." },
];

export const PRODUCT_STATUS_OPTIONS: HintOption[] = [
  { label: "Active", meaning: "Available to sell and attach to deals." },
  { label: "Inactive", meaning: "Hidden from new deals; keep for history." },
  { label: "In stock", meaning: "Ready to fulfill from inventory." },
  { label: "Out of stock", meaning: "Temporarily unavailable to sell." },
];

/** Button / control titles used on list rows and toolbars. */
export const ACTION_HINTS = {
  view_record: "Open this record. In this status it is read-only.",
  edit_record: "Open this record to change details.",
  delete_record:
    "Move this record to the recycle bin for 30 days. Restore it from Organization settings → Recycle bin.",
  mark_paid: "Record full payment for this issued invoice. Sets status to paid.",
  undo_paid: "Revert paid back to issued — use only to correct a mistaken payment.",
  generate_invoice: "Create an invoice from the order selected on the left.",
  choose_order: "Pick the order this new invoice should bill. Required before Generate invoice.",
  view_order: "Open this order read-only. Confirmed, fulfilled, and cancelled orders cannot be edited.",
  edit_order: "Open this pending order to update status, notes, or details.",
  filter_order_status: withOptions("Show only orders in this fulfilment status.", ORDER_STATUS_OPTIONS),
  filter_quote_status: withOptions("Show only quotes in this lifecycle status.", QUOTE_STATUS_OPTIONS),
  filter_lead_status: withOptions("Show only leads in this lifecycle status.", LEAD_STATUS_OPTIONS),
  filter_ticket_status: withOptions("Show only tickets in this status.", TICKET_STATUS_OPTIONS),
  filter_all_statuses: "Clear the status filter and show every record.",
  generate_lead: "Create a CRM lead from this support ticket so sales can follow up.",
  close_ticket: "Mark this ticket closed. You can add a closing comment first.",
  start_sales_cycle: "Begin the seven-stage playbook for the company chosen on the left.",
  choose_cycle_company: "Search and select the company this sales cycle will track.",
  cycle_settings: "Add, time, and retire the tasks in each sales-cycle stage. Org and app admins only.",
  flow_stage: "Open this stage’s tasks, the same board as clicking its slice on the cycle map.",
  cycle_progress: "Percent of all tasks across every stage marked Done. Updates as you move tasks.",
  close_stage_panel: "Hide the stage work panel. Task and stage progress stay saved.",
  filter_task_due: withOptions("Show only tasks whose due-date health matches.", [
    { label: "All", meaning: "No due-date filter." },
    { label: "Green", meaning: "On track — more than two days until due." },
    { label: "Amber", meaning: "Due soon — within the next two days." },
    { label: "Red", meaning: "Overdue — due today or already past." },
  ]),
  task_todo: "Move this task back to To do.",
  task_doing: "Mark this task as in progress (Doing).",
  task_done: "Mark this task complete (Done). Raises cycle progress.",
  task_comment: "Optional note saved with the next status change on this task.",
  map_sector: "Click to open this stage’s task board. Colour shows due-date health.",
  new_quote: "Create a draft quote for a company, then add line items.",
  create_quote: "Save the new draft quote and open it for editing.",
} as const;

export const FIELD_HINTS = {
  // Opportunities
  opportunity_title: "Short name for this deal — usually company + what they are buying.",
  opportunity_amount: "Expected deal value in the company currency. Used on boards, forecasts, and KPIs.",
  opportunity_value: "Expected deal value in the company currency. Used on boards, forecasts, and KPIs.",
  opportunity_expected_close: "Date you expect the deal to close. Drives forecast timing and overdue views.",
  opportunity_stage: withOptions(
    "Where this deal sits in your sales process. Moving stage updates the board and timeline.",
    OPPORTUNITY_STAGE_OPTIONS,
  ),
  opportunity_forecast_category: withOptions(
    "How this deal should count in the sales forecast — separate from stage. Stage is process; category is confidence for the period.",
    FORECAST_CATEGORY_OPTIONS,
  ),
  opportunity_lead: "The lead this deal was converted from. Linking a lead copies company and products when empty.",
  opportunity_company: "Account this deal belongs to. Required so revenue and activity roll up correctly.",
  company_lookup: "Search for an existing company and select a match. When create is allowed, an unmatched name becomes a new incomplete company on save.",
  opportunity_id: "System-generated reference (for example OPP0025). Use it when talking about this deal.",
  opportunity_source_lead: "Lead that was converted into this opportunity. Empty if the deal was created without a lead.",
  opportunity_products: "Catalogue items attached to this deal. Used when generating quotes.",
  opportunity_latest_quote: "Most recent quote generated from this deal.",

  // Leads
  lead_source: withOptions(
    "How this lead arrived. Helps marketing and sales see what is working.",
    LEAD_SOURCE_OPTIONS,
  ),
  lead_temperature: withOptions(
    "Your estimate of buying interest right now — not searched; use it to prioritize follow-up.",
    LEAD_TEMPERATURE_OPTIONS,
  ),
  lead_score: "Optional 0–100 score for priority. Higher means more likely or more valuable.",
  lead_notes: "What happened and what to do next — campaigns, replies, meeting outcomes.",
  lead_first_name: "Contact’s given name. Leave blank if this lead is company-only for now.",
  lead_last_name: "Contact’s family name.",
  lead_email: "Best email for follow-up. Matching emails can link an existing person.",
  lead_phone: "Best phone for follow-up.",
  lead_company: "Account this lead is about. New names create an incomplete company on save when allowed.",
  lead_products: "Products the lead is interested in — carried into the opportunity on convert.",

  // Companies
  company_legal_name: "Registered legal name of the account — the official name on contracts.",
  company_trading_name: "Name they trade as day-to-day, if different from the legal name.",
  company_code: "System-generated company reference.",
  company_status: withOptions(
    "Lifecycle of the account. Dormant and inactive can be set by hand, or by the quiet-account rules in Company settings.",
    COMPANY_STATUS_OPTIONS,
  ),
  lifecycle_watch: "Which records count as activity. The quiet clock uses the newest one of these.",
  lifecycle_dormant_days: "After this many days with nothing on the selected tabs, an active or prospect company becomes dormant.",
  lifecycle_inactive_days: "After this many days with nothing on the selected tabs, the company becomes inactive. Use a longer number than dormant.",
  lifecycle_winback: "When a company becomes inactive, start the chosen sales cycle. Leave this off to change the status without starting a cycle.",
  lifecycle_template: "Which sales cycle to start. Edit its stages and tasks on the Sales cycle tab. Discovery, KYC, and product positioning live there.",
  company_industry: "Sector or vertical — used for filtering and reporting.",
  company_website: "Public website URL.",
  company_phone: "Main switchboard or primary company phone.",
  company_email: "Main company email (not a personal contact email).",
  company_registration: "Company registration / company number in your jurisdiction.",
  company_vat: "Tax or VAT identifier used on invoices.",
  company_currency: "Default currency for deals, quotes, and invoices with this account.",
  company_payment_terms: "Standard payment terms (for example Net 30).",
  company_address: "Billing street address.",
  company_city: "Billing city.",
  company_state: "Billing state, province, or region.",
  company_country: "Billing country.",
  company_notes: "Internal notes about the account — not shown to the customer.",

  // Contacts
  contact_first_name: "Person’s given name.",
  contact_last_name: "Person’s family name.",
  contact_gender: "Optional. Used only where your team needs it for addressing or reporting.",
  contact_work_email: "Work email at their company — preferred for sales follow-up.",
  contact_personal_email: "Personal email if they prefer it, or as a backup.",
  contact_phone: "Best phone number for this person.",
  contact_company: "Company this person works for.",
  contact_role: "Job title or role at the company.",

  // Products
  product_name: "Customer-facing product or service name.",
  product_sku: "Stock-keeping unit or internal product code. Unique when set.",
  product_description: "Short description shown on quotes and pickers.",
  product_list_price: "Standard list / unit price before deal-specific discounts.",
  product_category: "Grouping for catalogues and filters (for example Software, Services).",
  product_status: withOptions(
    "Whether the product can be sold right now.",
    PRODUCT_STATUS_OPTIONS,
  ),
  product_search: "Filter the catalogue by name or SKU before adding a line.",
  product_choose: "Pick one matching product, then click Add. Repeat to attach more.",

  // Activities
  activity_type: withOptions(
    "What you did with the company or contact. Cold call, email, meeting, and demo are counted on the owner's sales plan.",
    ACTIVITY_TYPE_OPTIONS,
  ),
  activity_subject: "Short subject line for the activity.",
  activity_outcome: "What happened. Connected, Conversation, and Discovery count toward Meaningful conversations on the sales plan. A meeting outcome that starts with Demo also counts as a demo.",
  activity_due: "When this activity is due or was completed.",

  // Quotes / orders / invoices
  quote_issue_date: "Date the quote is considered issued to the customer.",
  quote_expiry_date: "Date after which the quote should be treated as expired.",
  quote_currency: "Currency for line prices and totals on this quote.",
  quote_status: withOptions("Lifecycle of the quote from draft through accept or reject.", QUOTE_STATUS_OPTIONS),
  quote_terms: "Commercial terms and conditions shown on the quote document.",
  quote_notes: "Internal notes about this quote — not a substitute for customer-facing terms.",
  order_status: withOptions("Fulfilment state of the order after a quote is accepted.", ORDER_STATUS_OPTIONS),
  order_notes: "Internal notes about fulfilment — not a substitute for quote terms.",
  order_number: "System-generated order reference.",
  invoice_status: withOptions("Whether the invoice is draft, issued, paid, or void.", INVOICE_STATUS_OPTIONS),
  invoice_number: "System-generated invoice reference.",
  invoice_total: "Amount due on this invoice, in the invoice currency.",

  // Support
  ticket_number: "System-generated support ticket reference.",
  ticket_title: "Short summary of the issue.",
  ticket_description: "Full description of the problem the customer reported.",
  ticket_status: withOptions("Open, waiting, or closed — drives aging and queues.", TICKET_STATUS_OPTIONS),
  ticket_aging: "How long the ticket has been open since it was created.",

  // Sales cycle / filters
  sales_cycle_started: "Whether the guided sales-cycle run has been started for this company.",
  sales_cycle_run_status: withOptions("Overall state of this company’s sales-cycle run.", CYCLE_RUN_STATUS_OPTIONS),
  sales_cycle_stage_status: withOptions(
    "Whether this stage has been started in the timed flow. Done stages are locked.",
    CYCLE_STAGE_STATUS_OPTIONS,
  ),
  sales_cycle_task_status: withOptions("Where this checklist item sits on the stage board.", CYCLE_TASK_STATUS_OPTIONS),
  sales_cycle_health: withOptions("SLA health of the current stage or task due date.", [
    { label: "All", meaning: "No health filter." },
    { label: "Green", meaning: "On track — more than two days until due." },
    { label: "Amber", meaning: "Due soon — within the next two days." },
    { label: "Red", meaning: "Overdue — due today or already past." },
  ]),
  lead_status: withOptions("Where this lead sits before conversion or disqualification.", LEAD_STATUS_OPTIONS),

  // Sales cycle settings — per-stage SLA hours
  cycle_stage_lead_identification:
    "Hours to capture the potential customer and contacts, plus a Customer Profile (KYC) and Customer Health check, before moving on.",
  cycle_stage_customer_contact:
    "Hours to set up the intro call, understand pain points, and send the follow-up email from meeting notes.",
  cycle_stage_qualify_the_lead: "Hours to confirm offering fit and budget before treating the deal as qualified.",
  cycle_stage_sales_presentation: "Hours to arrange and deliver the demo and client meeting.",
  cycle_stage_overcome_objections: "Hours to send the quote and work through any objections raised.",
  cycle_stage_close_the_sale: "Hours to get the order issued once the customer agrees.",
  cycle_stage_follow_up: "Hours after close to request a customer reference or referral.",
  cycle_stage_generic:
    "How long this stage should take. It is the sum of the active tasks, and the next stage starts when that time is up.",
  cycle_territory:
    "The sales cycle for this territory. Companies in the territory start on it. Other territories keep their own. A territory you have not opened still follows the default cycle. An app admin can open and change any territory's cycle; the default cycle is organization admin only. Enterprise plan.",
  cycle_task_title: "What the salesperson does in this stage. Shown on the cycle task board.",
  cycle_task_estimate:
    "How long this task should take. The stage length is the sum of its active tasks, and later stages start after that.",
  cycle_task_unit: "Hours, days, weeks, or months. A day is 24 hours, a week is 7 days, and a month is 30 days.",
  cycle_task_active:
    "Take this task out of the cycle. It stays on file so you can restore it. Tasks nobody has started drop off open cycles when you save.",

  // Sales cycle settings — per-stage entry rules
  cycle_rule_enabled: "Turn this stage's automation on or off. Off means no task or email is created when a deal enters this stage.",
  cycle_rule_create_task: "When a deal enters this stage, add a cycle task with the title and due hours set below.",
  cycle_rule_queue_email_draft: "When a deal enters this stage, prepare an email draft (subject and body below) instead of sending automatically — a person still sends it.",
  cycle_rule_task_title: "Title shown on the created task. Use {{title}} for the deal name and {{stage}} for the stage name.",
  cycle_rule_due_hours: "How many hours after the deal enters this stage the created task is due.",
  cycle_rule_email_subject: "Subject line of the queued email draft. Supports {{title}} and {{stage}}.",
  cycle_rule_email_body: "Body text of the queued email draft. Supports {{title}} and {{stage}}.",

  // Loss reason settings
  loss_reason_label: "Name shown as a checkbox option when a deal is closed as lost. Keep it short — it appears on the Closed Lost dialog and in loss-reason reports.",
  loss_reason_tooltip: "Longer explanation shown on hover when someone picks this reason, so the team applies it consistently instead of guessing from the label alone.",

  // Board display settings (Leads / Opportunities Kanban)
  board_retain_days: "How many days a card stays visible on the board after it reaches this terminal state, before a background sweep hides it. 1 keeps it for the rest of that calendar day; 0 hides it on the next sweep, effectively immediately.",
  board_aging_band_days: "Upper bound in days for this aging band. Any open card younger than this threshold (and older than the previous band) is painted with this band's colour — a fast, at-a-glance read on which deals or leads are going stale.",
  board_aging_band_color: "Background colour applied to open cards whose age falls in this band. Bands are checked in order, so pick colours that get progressively more alarming (e.g. green to amber to red) as bands increase.",
  board_terminal_color: "Solid background colour for cards that have reached this terminal stage. Aging colours never apply here — a closed card's colour is fixed, not time-based.",

  // Company workflow settings (reminders / opportunity aging)
  workflow_interval_days: "How often, in days, this reminder re-fires for a record that still matches its trigger condition. Shorter intervals nag more often; longer intervals give people more room before a repeat nudge.",
  workflow_enabled: "Turn this reminder rule on or off. Off means the interval above is ignored and nothing fires, but the rule and its interval are kept for when you re-enable it.",

  // App Designer (field/screen designer)
  designer_field_required: "Require a value in this field before the record can be saved. Turning this on for a field already in use can block editing existing records that are missing it until they're filled in.",
  designer_field_width: "How much of the row this field's box takes up. Half row lets two fields sit side by side; Full row gives one field the entire width — useful for long text like addresses or notes.",
  designer_field_options: "The choices shown in this field's dropdown, separated by commas. For fields the board or Kanban reads (like Lead status), these values become the board's columns, so renaming or removing one here can move or hide cards already on that value.",
  designer_field_type: "Underlying data type stored for this field (text, number, date, select, etc). Native platform fields keep their original type and can't be changed; only fields you added yourself are editable here.",
  designer_field_section: "Which section of this screen the field is placed in. Sections group related fields visually — move a field here to reorganize the layout without deleting and re-adding it.",
  designer_section_title: "Heading shown above this group of fields on the record screen. Purely organizational — it doesn't change what data is captured.",

  // Document designer
  document_block_label: "Heading or caption shown above this block on the printed document. Leave the default unless you want different wording on the customer-facing page.",
  document_field_picker: "Which record field this block prints. Choosing a different field replaces the value shown without changing the block's position or styling.",
  document_calc_operation: "Whether this calculation is a percentage of another amount (Percent of base) or a fixed number you type in (Fixed amount).",
  document_calc_base: "The amount this percentage is calculated against — subtotal, total, tax, or discount — so you can build rules like \"5% of subtotal\" versus \"5% of total\".",
  document_calc_apply_as: "How this calculated amount affects the document total: Discount subtracts it, Tax/Surcharge add it, and Display only shows it as a line without changing the total.",
  document_page_size: "Paper size used when this document is generated as a PDF — A4 or Letter. Match whatever your customers and printers expect in your region.",

  // Dashboard designer
  dashboard_chart_type: "Visual shape used to plot the grouped data below — bar, line, pie, funnel, etc. Pick the type that best matches what you're comparing (trends over time favour line/area; part-of-whole favours pie/doughnut).",
  dashboard_chart_group_by: "The field used to split data into the chart's categories or slices — for example grouping opportunities by stage or leads by source.",
  dashboard_chart_value_field: "The number being measured for each group. Leave blank to just count records in each group instead of summing or averaging a field.",
  dashboard_chart_aggregation: "How values within each group are combined — count, sum, average, min, or max. Sum only makes sense once a value field is chosen.",
  dashboard_chart_date_field: "Optional date field to bucket results by (for example by month), instead of or alongside the group-by field. Leave as None for a simple grouped chart.",
  dashboard_chart_color_scheme: "Palette applied to this chart's series or slices. Pick a scheme with enough distinct colours for the number of categories so nothing blends together.",
  dashboard_chart_show_legend: "Show the colour-to-category key beside the chart. Turn off for a small chart where the legend would crowd out the data.",
  dashboard_chart_show_values: "Print each bar/slice's number directly on the chart, in addition to the axis or legend. Useful for exact figures at a glance; can clutter charts with many categories.",
  dashboard_chart_value_format: "How the printed values (when Show values is on) are formatted — a plain number, a percentage of the total, or money in the organisation currency from Settings.",
  dashboard_kpi_dataset: "Which record type this KPI counts or sums from (for example Opportunities or Leads). Changing it resets the value and date field below, since they belong to the previous dataset.",
  dashboard_kpi_width: "How many of the row's 7 columns this KPI card occupies. Larger widths suit a headline number; smaller widths let several KPIs sit in one row.",
  dashboard_field_display_name: "Optional label override shown on chart axes and legends instead of the raw field name — use it to show something more readable to viewers.",
  dashboard_axis_unit: "Optional unit for the numbers on this chart. $ means money and is shown in the organisation currency from Settings. Other units, such as hrs or kg, are shown as typed.",
  dashboard_table_columns: "Which fields appear as columns in this table section, in the order ticked. Only fields from the table's detected record type are offered.",

  // Organization settings — app timezone/currency/fiscal year
  org_timezone: "Default timezone applied to new activity and due-date calculations across the organization. People can still set their own timezone under User settings for how dates display to them.",
  org_currency: "Default currency for new deals, quotes, orders, and invoices. Changing it does not convert amounts already stored in the previous currency.",
  org_date_format: "How dates are ordered on screen, for example 10/25/2026 or 25/10/2026. Stored dates stay in a standard form. People can override this under User settings.",
  org_time_format: "12-hour (3:40 PM) or 24-hour (15:40) for times shown on screen. The stored time does not change.",
  org_number_format: "Which symbols separate thousands and decimals on screen, for example 1,234.56 or 1.234,56. Amounts are still stored as plain numbers.",
  org_fiscal_year_start: "Month your fiscal year begins. Drives every quarter-based report (Forecast, and the Opportunities/Leads projected-vs-target charts) — set it to January if you want quarters to match the calendar year.",
  org_history_after_days: "How many days a finished quote (order created), order (invoiced) or invoice (paid) stays in the open list before moving to the History table on its page. 0 moves it immediately. Nothing is deleted.",
  org_quote_approval_required: "When on, a Sales Manager must approve a quote before it can be sent to the customer, adding a review step to the golden thread. When off, a rep can send a quote as soon as it's ready.",
  org_tax_rate: "Default tax percentage applied to new quotes, orders, and invoices. Leave blank to omit tax from those documents entirely — this is a starting default, not a lock, so individual documents can still be adjusted.",

  // Organization settings — SSO
  sso_enabled: "Turn single sign-on on or off for this organization. While on, members can continue from the login screen using their email; the IdP's email must match an existing account.",
  sso_protocol: "Which SSO standard your identity provider speaks. OpenID Connect (OIDC) uses Authorization Code with PKCE; SAML 2.0 uses a signed XML assertion. Pick whichever your IdP issues — don't guess, check your IdP's app-integration docs.",
  sso_domains: "Email domains allowed to sign in via SSO, comma or space separated. Leave blank to allow any domain the IdP authenticates — scoping this down stops someone else's tenant on the same IdP from reaching your org.",
  sso_metadata_url: "The IdP's published metadata document. For OIDC this is the well-known OpenID configuration URL; for SAML it's the IdP metadata XML URL. Most IdPs can autofill the rest of the fields below from this.",
  sso_issuer: "The IdP's issuer / entity identifier (OIDC) or SSO URL (SAML) — must match exactly what the IdP presents in its tokens or assertions, or sign-in will be rejected.",
  sso_client_id: "OIDC client ID issued by the identity provider when you registered this application with them.",
  sso_client_secret: "OIDC client secret issued by the identity provider. Leave blank when saving other changes to keep the secret already stored — it is never shown back to you once saved.",
  sso_scopes: "OIDC scopes requested during login. `openid email profile` is enough for identity-first sign-in; only add more if your IdP requires it for group or role claims.",
  sso_redirect_uri: "Callback URL this platform expects the IdP to redirect back to after login. Paste this exact value into the IdP's app configuration — it is generated here, not something you choose.",
  sso_entity_id: "This platform's SAML service-provider entity ID, registered with the IdP so it knows which app is requesting sign-in.",
  sso_acs_url: "Assertion Consumer Service URL — where the IdP posts the signed SAML assertion after login. Paste this exact value into the IdP's app configuration.",
  sso_certificate: "The IdP's public signing certificate, used to verify that SAML assertions really came from your IdP and weren't forged. Leave blank when saving other changes to keep the certificate already stored.",

  // Organization settings — AI drafts
  ai_drafts_enabled: "Turn on AI-assisted draft text (for example suggested email replies) across the organization. Usage is metered against the monthly budget below.",
  ai_drafts_budget: "Monthly spending cap, in USD, for AI draft generation. Once used this period reaches the budget, drafting pauses until the next month or a higher budget is saved.",
  ai_drafts_provider: "Which AI provider services draft requests. Only change this if you've been told to — an incorrect provider name will fail every draft request.",

  // Organization settings — data import
  import_kind: "Which kind of data this template and file covers — CRM records, user accounts, or roles. Choosing a different kind changes the template you download and the columns expected on upload.",
  import_column_mapping: "Which column in your uploaded file supplies this platform field. Leave Not mapped for fields you don't have data for; unmapped required fields will show as issues after Re-check mapping.",

  // Organization settings — network allowlist
  network_allowlist_entry: "An IP address or CIDR range allowed to sign in to this organization (for example 203.0.113.10 for one address, or 10.0.0.0/24 for a range). Once any entry is added, sign-in is restricted to only the listed networks — this does not affect platform admin access.",

  // People / roles settings
  people_role_checkbox: "Grants this role to the person, adding whatever tabs the role includes to the ones from their other roles. Roles are additive — removing one role doesn't remove a tab still granted by another.",
  people_tab_checkbox: "Include this tab in the role, so anyone with the role can open it. Tabs beyond the organization's current plan stay visible (greyed with a plan badge) so they light up automatically after an upgrade, without editing the role again.",
  people_tab_admin_checkbox: "Grant admin rights on this tab only — configure, merge, restore, and delete — without granting admin on any other tab. Requires the tab itself to be included in the role first.",
  people_org_admin: "Grants full administrative access to every tab and setting in this organization, regardless of assigned roles. Reserve this for people who should be able to change billing, users, and security settings, not just use the app.",
  people_reports_to: "This person's manager in the approval chain — used for timesheet/expense approvals and reporting rollups. Leave as None for someone at the top of the chain.",
  people_territory: "The territory this person works in. It decides which companies, leads, deals, quotes and orders they can see: a sales person sees the shared records and their own and their reports' pipeline there, and a sales manager sees everything in it. Global is a territory like the others, not \"see everything\" — organization admins, app admins and roles set to every territory see all territories. Without the Enterprise plan everyone is on Global.",
  people_role_record_scope: "Which records people with this role see. Own and direct reports: their own leads, deals and quotes, plus those of people who report to them (a sales person). Everything in their territories: every record in the territories they are assigned to (a sales manager). Everything in every territory: every record in the organization, for a director or analyst who needs to see it all without app admin rights to change settings. When someone holds several roles, the widest one applies. Application roles such as Sales person and Sales manager have this fixed, and it cannot be changed. Create a custom role for a different one.",
} as const;

export type FieldHintKey = keyof typeof FIELD_HINTS;

export function fieldHint(key: FieldHintKey): string {
  return FIELD_HINTS[key];
}

/** Resolve a hint from the catalogue, designed-field meaning, or an explicit override. */
/** Map designed/app-designer field_keys onto catalogue entries. */
const FIELD_KEY_ALIASES: Record<string, FieldHintKey> = {
  legal_name: "company_legal_name",
  trading_name: "company_trading_name",
  company_code: "company_code",
  account_status: "company_status",
  industry: "company_industry",
  website: "company_website",
  primary_phone: "company_phone",
  primary_email: "company_email",
  registration_number: "company_registration",
  vat_number: "company_vat",
  currency: "company_currency",
  payment_terms: "company_payment_terms",
  billing_address_line1: "company_address",
  billing_address_city: "company_city",
  billing_address_state: "company_state",
  billing_address_country: "company_country",
  notes: "company_notes",
  title: "opportunity_title",
  value: "opportunity_value",
  expected_close_date: "opportunity_expected_close",
  stage: "opportunity_stage",
  forecast_category: "opportunity_forecast_category",
  lead_source: "lead_source",
  rating: "lead_temperature",
  lead_score: "lead_score",
  first_name: "lead_first_name",
  last_name: "lead_last_name",
  email: "lead_email",
  phone: "lead_phone",
  work_email: "contact_work_email",
  personal_email: "contact_personal_email",
  gender: "contact_gender",
  sku: "product_sku",
  description: "product_description",
  unit_price: "product_list_price",
  product_category: "product_category",
  product_status: "product_status",
  name: "product_name",
  activity_type: "activity_type",
  subject: "activity_subject",
  outcome: "activity_outcome",
  quote_status: "quote_status",
  order_status: "order_status",
  invoice_status: "invoice_status",
  lead_status: "lead_status",
  status: "ticket_status",
};

export function resolveFieldHint(
  key: string | null | undefined,
  fallback?: string | null,
): string | undefined {
  if (key) {
    if (key in FIELD_HINTS) return FIELD_HINTS[key as FieldHintKey];
    const alias = FIELD_KEY_ALIASES[key];
    if (alias) return FIELD_HINTS[alias];
  }
  if (fallback?.trim()) return fallback.trim();
  return undefined;
}
